import { ColumnProps } from "antd/lib/table";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import ProductContext from "../../../context/product/context";
import { ImCheckmark, ImCross } from "react-icons/im";
import { IProduct } from "../../../models/product/response";
import Tag from "../../general/antd/tag";
import { Permissions } from "../../../utils/Permissions/Permissions";
import { FaMinus } from "react-icons/fa";
import {
  BooleanStatus,
  getProductStatus,
  getProductStatusColor,
  getProductType,
  getProductTypeColor,
} from "../../../models/product/enum";
import { MoreOutlined } from "@ant-design/icons";
import { Card, Col, Dropdown, Menu, Row } from "antd";
import Button from "../../../components/general/antd/button";
import { QuestionCircleTwoTone } from "@ant-design/icons";
import Popconfirm from "../../general/antd/pop-confirm";
import Image from "../../general/antd/image";
import AuthContext from "../../../context/auth/context";
import AddQuantityButton from "../../general/antd/add-quantity-button";
import MatchQuantityButton from "../../general/antd/match-quantity-button";
import TransferBatchButton from "../../general/antd/transfer-batch-button";
import WriteOffBatchButton from "../../general/antd/write-off-batch-button";
import AppContext from "../../../context/app/context";
import IdAccountingButton from "../../general/antd/send-id-accounting";

interface IProps { }

const ProductTable: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(ProductContext);
  const { screenSize } = useContext(AppContext);

  const { userPermissions: permissions } = useContext(AuthContext);

  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];
  const hasPurchasePriceColumns = (list?.data ?? []).some((product) =>
    (product.storeQuantities ?? []).some(
      (quantity) =>
        Object.prototype.hasOwnProperty.call(quantity, "purchase_price") ||
        Object.prototype.hasOwnProperty.call(quantity, "purchase_price_in_dollar")
    )
  );
  const getPriceRangeLabel = (prices: number[]) => {
    if (prices.length === 0) return "-";
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return min.toLocaleString();
    return `${min.toLocaleString()} - ${max.toLocaleString()}`;
  };
  const getDollarPriceRangeLabel = (prices: number[]) => {
    if (prices.length === 0) return "-";
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return `$${min}`;
    return `$${min} - $${max}`;
  };
  const columns: ColumnProps<IProduct>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("barcode"),
      dataIndex: "barcode",
      key: "barcode",

      width: 100,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: {
        multiple: 2,
      },
    },
    {
      key: "image",
      title: t("image"),
      dataIndex: "brandImage",
      align: "center",
      render: (_, record) => {
        return record?.image ? (
          <Image src={record.image?.url} height={"25px"} preview={true} />
        ) : (
          "-"
        );
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      align: "center",
      key: "status",
      render: (_, record) => {
        return (
          <Tag
            title={getProductStatus(record?.status)}
            color={getProductStatusColor(record?.status)}
          />
        );
      },
    },
    {
      title: t("type"),
      dataIndex: "type",
      width: 200,
      align: "center",
      key: "type",
      render: (_, record) => {
        return (
          <Tag
            title={getProductType(record?.type)}
            color={getProductTypeColor(record?.type)}
          />
        );
      },
    },
    {
      title: t("main_category"),
      dataIndex: "main_category",
      align: "center",
      key: "main_category",
      render: (_, record) => {
        return <>{record?.mainCategory?.name ?? "-"}</>;
      },
    },
    {
      title: t("parent_category"),
      dataIndex: "parent_category",
      align: "center",
      key: "sub_category",
      render: (_, record) => {
        return <>{record?.parentCategory?.name ?? "-"}</>;
      },
    },
    {
      title: t("new"),
      dataIndex: "isNew",

      align: "center",
      key: "isNew",
      render: (_, record) => {
        return (
          <>
            {record?.isNew ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("trademark"),
      dataIndex: "trademark",
      align: "center",
      key: "trademark",
      render: (_, record) => {
        return <>{record?.trademark?.name ?? "-"}</>;
      },
    },
    {
      title: t("available"),
      dataIndex: "available",
      align: "center",
      key: "isBreif",
      render: (_, record) => {
        const quantities = record.storeQuantities || [];

        if (quantities.length === 0) {
          return <ImCross color="red" />;
        }

        const allAvailable = quantities.every(q => q.available);
        const noneAvailable = quantities.every(q => !q.available);

        if (allAvailable) {
          return <ImCheckmark color="green" />;
        } else if (noneAvailable) {
          return <ImCross color="red" />;
        } else {
          return <FaMinus color="orange" />;
        }
      }
    },
    {
      title: t("store_status"),
      dataIndex: "storeStatus",
      align: "center",
      key: "storeStatus",
      render: (_, record) => {
        const quantities = record.storeQuantities || [];

        if (quantities.length === 0) {
          return <ImCross color="red" />;
        }

        const allActive = quantities.every(q => q.status === BooleanStatus.ACTIVE);
        const allInactive = quantities.every(q => q.status === BooleanStatus.INACTIVE);

        if (allActive) {
          return <ImCheckmark color="green" />;
        } else if (allInactive) {
          return <ImCross color="red" />;
        } else {
          return <FaMinus color="orange" />;
        }
      }
    },
    {
      title: t("need_recipe"),
      dataIndex: "need_recipe",

      align: "center",
      key: "isBreif",
      render: (_, record) => {
        return (
          <>
            {record.needRecipe ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("sold_by_unit"),
      dataIndex: "sold_by_unit",

      align: "center",
      key: "isBreif",
      render: (_, record) => {
        return (
          <>
            {record.soldByUnit ? (
              <ImCheckmark color="green" />
            ) : (
              <ImCross color="red" />
            )}
          </>
        );
      },
    },
    {
      title: t("price_range"),
      dataIndex: "priceRange",
      align: "center",
      key: "priceRange",
      render: (_, record) => {
        const prices =
          record.storeQuantities?.map((sq) => sq.price).filter((p): p is number => p != null) || [];
        return getPriceRangeLabel(prices);
      },
    },
    {
      title: t("price_range_dollar"),
      dataIndex: "priceRangeDollar",
      align: "center",
      key: "priceRangeDollar",
      render: (_, record) => {
        const prices =
          record.storeQuantities
            ?.map((sq) => sq.price_in_dollar)
            .filter((p): p is number => p != null) || [];
        return getDollarPriceRangeLabel(prices);
      },
    },
    ...(hasPurchasePriceColumns
      ? [
        {
          title: t("purchase_price_range"),
          dataIndex: "purchasePriceRange",
          align: "center" as const,
          key: "purchasePriceRange",
          render: (_: unknown, record: IProduct) => {
            const prices =
              record.storeQuantities
                ?.map((sq) => sq.purchase_price)
                .filter((p): p is number => p != null) || [];
            return getPriceRangeLabel(prices);
          },
        },
        {
          title: t("purchase_price_range_dollar"),
          dataIndex: "purchasePriceRangeDollar",
          align: "center" as const,
          key: "purchasePriceRangeDollar",
          render: (_: unknown, record: IProduct) => {
            const prices =
              record.storeQuantities
                ?.map((sq) => sq.purchase_price_in_dollar)
                .filter((p): p is number => p != null) || [];
            return getDollarPriceRangeLabel(prices);
          },
        },
      ]
      : []),
    {
      title: t("special_discount"),
      dataIndex: "specialDiscount",
      align: "center",
      key: "specialDiscount",
      render: (_, record) => {
        const quantities = record.storeQuantities || [];
        if (quantities.length === 0) return <ImCross color="red" />;
        const hasAnySpecialDiscount = quantities.some(q => q.special_discount);
        return hasAnySpecialDiscount ? (
          <ImCheckmark color="green" />
        ) : (
          <ImCross color="red" />
        );
      },
    },
    {
      title: t("value_special_discount") + " " + "(%)",
      dataIndex: "valueSpecialDiscount",
      align: "center",
      key: "valueSpecialDiscount",
      render: (_, record) => {
        const values = record.storeQuantities
          ?.filter(sq => sq.special_discount)
          .map(sq => sq.value_special_discount) || [];
        if (values.length === 0) return "-";
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (min === max) return `${min}%`;
        return `${min}% - ${max}%`;
      },
    },
    {
      title: t("general_discount"),
      dataIndex: "generalDiscount",
      align: "center",
      key: "generalDiscount",
      render: (_, record) => {
        const quantities = record.storeQuantities || [];
        if (quantities.length === 0) return <ImCross color="red" />;
        const hasAnyGeneralDiscount = quantities.some(q => q.general_discount);
        return hasAnyGeneralDiscount ? (
          <ImCheckmark color="green" />
        ) : (
          <ImCross color="red" />
        );
      },
    },
    {
      title: t("value_general_discount") + " " + "(%)",
      dataIndex: "valueGeneralDiscount",
      align: "center",
      key: "valueGeneralDiscount",
      render: (_, record) => {
        const values = record.storeQuantities
          ?.filter(sq => sq.general_discount)
          .map(sq => sq.value_general_discount) || [];
        if (values.length === 0) return "-";
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (min === max) return `${min}%`;
        return `${min}% - ${max}%`;
      },
    },
    {
      title: t("actions"),
      width: screenSize != "mobileOrTablet" ? 250 : 90,
      dataIndex: "",
      align: "center",
      fixed: "right",
      key: "x",
      render: (_: any, record) => {
        const isSmallScreen = screenSize == "mobileOrTablet";
        const dropdownMenu = (
          <Menu>
            {isSmallScreen && (
              <>
                <Menu.Item key="view">
                  <span
                    onClick={(e) => {
                      const url = `/products/details/${record?.id}`;
                      if (e.ctrlKey || e.metaKey) {
                        window.open(url, "_blank");
                      } else {
                        navigate(url);
                      }
                    }}
                  >
                    {t("view")}
                  </span>
                </Menu.Item>
                <Menu.Item key="edit">
                  <span
                    onClick={(e) => {
                      const url = `/products/update/${record?.id}`;
                      if (e.ctrlKey || e.metaKey) {
                        window.open(url, "_blank");
                      } else {
                        navigate(url);
                      }
                    }}
                  >
                    {t("edit")}
                  </span>
                </Menu.Item>
                <Menu.Item key="delete">
                  <DeleteBtn
                    onConfirm={async () => {
                      await actions.deleteProduct(record?.id);
                      eventManager.emit(EVENT_SUCCESS);
                    }}
                    loading={loading.includes("delete")}
                  />
                </Menu.Item>
              </>
            )}

            {/* العمليات العامة لجميع الشاشات */}
            <Menu.Item key="circulate">
              <Popconfirm
                title={t("confirm")}
                okButtonProps={{
                  danger: true,
                  loading: loading.includes("update"),
                }}
                icon={<QuestionCircleTwoTone twoToneColor="red" />}
                onConfirm={async () => {
                  await actions.circulateProduct(record?.id);
                  eventManager.emit(EVENT_SUCCESS);
                }}
              >
                <span>{t("circulate_price")}</span>
              </Popconfirm>
            </Menu.Item>

            {userPermissions.includes(Permissions.CAN_MATCH_PRODUCT_ACCOUNTING_ID) && (
              <Menu.Item key="id_accounting">
                <IdAccountingButton quantities={record.storeQuantities} passedId={record?.id} type="text">
                  {t("send_id_accounting")}
                </IdAccountingButton>
              </Menu.Item>
            )}

            {userPermissions.includes(Permissions.CAN_ADD_PRODUCT_QUANTITY) && (
              <Menu.Item key="add_quantity">
                <AddQuantityButton quantities={record.storeQuantities} passedId={record?.id} type="text">
                  {t("add_quantity")}
                </AddQuantityButton>
              </Menu.Item>
            )}

            {userPermissions.includes(Permissions.CAN_MATCH_PRODUCT_QUANTITY) && (
              <Menu.Item key="match_quantity">
                <MatchQuantityButton quantities={record.storeQuantities} passedId={record?.id} type="text">
                  {t("match_quantity")}
                </MatchQuantityButton>
              </Menu.Item>
            )}

            {userPermissions.includes(Permissions.CAN_TRANSFER_WMS_BATCH) && (
              <Menu.Item key="transfer_batch">
                <TransferBatchButton quantities={record.storeQuantities} passedId={record?.id} type="text">
                  {t("transfer_batch")}
                </TransferBatchButton>
              </Menu.Item>
            )}

            {userPermissions.includes(Permissions.CAN_WRITE_OFF_WMS_BATCH) && (
              <Menu.Item key="write_off_batch">
                <WriteOffBatchButton quantities={record.storeQuantities} passedId={record?.id} type="text">
                  {t("write_off_batch")}
                </WriteOffBatchButton>
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Space>
            {!isSmallScreen && (
              <>
                <ViewBtn
                  onClick={(e) => {
                    const url = `/products/details/${record?.id}`;
                    if (e.ctrlKey || e.metaKey) {
                      window.open(url, "_blank");
                    } else {
                      navigate(url);
                    }
                  }}
                />
                <EditBtn
                  onClick={(e) => {
                    const url = `/products/update/${record?.id}`;
                    if (e.ctrlKey || e.metaKey) {
                      window.open(url, "_blank");
                    } else {
                      navigate(url);
                    }
                  }}
                />
                <DeleteBtn
                  onConfirm={async () => {
                    await actions.deleteProduct(record?.id);
                    eventManager.emit(EVENT_SUCCESS);
                  }}
                  loading={loading.includes("delete")}
                />
              </>
            )}

            <Dropdown overlay={dropdownMenu} trigger={["click"]}>
              <Button shape="circle" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        );
      }

    },
  ];

  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        scroll={{
          x: 2100,
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters
          );
        }}
      />
    </>
  );
};

export default ProductTable;

